'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { Notification } from '@/ui/notification/Notification'
import { Tag } from '@/ui/tag/Tag.client'
import { TagGroup } from '@/ui/tag/TagGroup.client'
import type { Key } from 'react-aria-components'
import { Button, Group, SelectValue } from 'react-aria-components'
import {
  SELECT_ALL_ID,
  SELECT_FIELD_HEIGHT,
  SELECT_FIELD_PADDING,
  selectFieldCva,
  selectValueCva,
} from '../select.styles'
import { SelectTriggerChrome } from './SelectTriggerChrome.client'

type SelectTagItem = {
  id: Key
  label: string
}

type SelectMultipleTriggerProps = {
  inputId: string
  isOpen: boolean
  hasError: boolean
  isDisabled: boolean
  showLoader: boolean
  notificationNumber?: number
  /** @default true — when false, shows `displayLabel` (e.g. "3 roles selected") instead of tags. */
  withTags?: boolean
  /** Shown when `withTags` is false. Usually the dynamic `placeholder(count)` string. */
  displayLabel?: string
  tagsAriaLabel: string
  lockedTagIds?: ReadonlySet<string>
  /** Keeps RHF in sync when a tag is removed (do not rely on `state.setValue` alone). */
  onRemoveKeys: (keys: Set<Key>) => boolean
}

function SelectedTags({
  tags,
  tagsAriaLabel,
  lockedTagIds,
  onRemove,
}: {
  tags: SelectTagItem[]
  tagsAriaLabel: string
  lockedTagIds?: ReadonlySet<string>
  onRemove: (keys: Set<Key>) => void
}) {
  return (
    <TagGroup size="md" aria-label={tagsAriaLabel} items={tags} onRemove={onRemove}>
      {(item) => (
        <Tag id={String(item.id)} isDisabled={lockedTagIds?.has(String(item.id))}>
          {item.label}
        </Tag>
      )}
    </TagGroup>
  )
}

export function SelectMultipleTrigger({
  inputId,
  isOpen,
  hasError,
  isDisabled,
  showLoader,
  notificationNumber,
  withTags = true,
  displayLabel,
  tagsAriaLabel,
  lockedTagIds,
  onRemoveKeys,
}: SelectMultipleTriggerProps) {
  const fieldClassName = ({
    isFocusVisible,
    isDisabled: racDisabled,
  }: {
    isFocusVisible: boolean
    isDisabled?: boolean
  }) =>
    twMerge(
      'flex w-full items-center justify-between gap-1 rounded-sm',
      withTags ? 'h-auto min-h-6 py-1' : SELECT_FIELD_HEIGHT,
      SELECT_FIELD_PADDING,
      selectFieldCva({
        isOpen: racDisabled || isDisabled ? false : isOpen,
        isFocusVisible: racDisabled || isDisabled ? false : isFocusVisible,
        hasError: racDisabled || isDisabled ? false : hasError,
        isDisabled: racDisabled || isDisabled,
      }),
    )

  if (!withTags) {
    return (
      <Button id={inputId} className={fieldClassName}>
        <span className="flex min-w-0 flex-1 items-center gap-1">
          <SelectValue
            className={twMerge(
              'min-w-0 flex-1 truncate text-left text-md font-medium leading-light',
              selectValueCva({ isDisabled }),
            )}
          >
            {({ defaultChildren }) => displayLabel || defaultChildren}
          </SelectValue>
          {notificationNumber != null && (
            <Notification
              number={notificationNumber}
              aria-label={String(notificationNumber)}
              className="shrink-0"
            />
          )}
        </span>
        <SelectTriggerChrome showLoader={showLoader} isDisabled={isDisabled} isOpen={isOpen} />
      </Button>
    )
  }

  // Overlay Button fills the Group so RAC --trigger-width matches the field, while Tag
  // dismiss stays a sibling (valid HTML). See Select#taggroup + react-spectrum#9451.
  return (
    <Group
      className={twMerge(
        'relative flex w-full items-center justify-between gap-1 rounded-sm',
        'h-auto min-h-6 py-1',
        SELECT_FIELD_PADDING,
        isDisabled && 'pointer-events-none',
      )}
    >
      <Button
        id={inputId}
        className={({ isFocusVisible }) =>
          twMerge(
            'z-0 absolute inset-0 rounded-sm',
            selectFieldCva({
              isOpen: isDisabled ? false : isOpen,
              isFocusVisible: isDisabled ? false : isFocusVisible,
              hasError: isDisabled ? false : hasError,
              isDisabled,
            }),
          )
        }
      />
      <span className="z-10 pointer-events-none relative flex min-w-0 flex-1 items-center gap-1">
        <SelectValue
          className={twMerge(
            'min-w-0 flex-1 text-left text-md font-medium leading-light',
            selectValueCva({ isDisabled }),
          )}
        >
          {({ state, isPlaceholder, defaultChildren }) => {
            const tags: SelectTagItem[] = state.selectedItems
              .filter((node) => String(node.key) !== SELECT_ALL_ID)
              .map((node) => ({
                id: node.key,
                label: node.textValue || String(node.key),
              }))

            if (isPlaceholder || tags.length === 0) return defaultChildren

            return (
              <div className="pointer-events-auto">
                <SelectedTags
                  tags={tags}
                  tagsAriaLabel={tagsAriaLabel}
                  lockedTagIds={lockedTagIds}
                  onRemove={(keys) => {
                    if (!onRemoveKeys(keys)) return

                    const value = state.value
                    if (!Array.isArray(value)) return

                    state.setValue(value.filter((key) => !keys.has(key)))
                  }}
                />
              </div>
            )
          }}
        </SelectValue>
        {notificationNumber != null && (
          <Notification
            number={notificationNumber}
            aria-label={String(notificationNumber)}
            className="shrink-0"
          />
        )}
      </span>
      <span className="z-10 pointer-events-none relative">
        <SelectTriggerChrome showLoader={showLoader} isDisabled={isDisabled} isOpen={isOpen} />
      </span>
    </Group>
  )
}
